import { type ButtonHTMLAttributes, type KeyboardEvent, type MouseEvent, type ReactNode, useRef } from 'react';
import cn from 'clsx';
import { cva } from 'class-variance-authority';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import AiCross from '@pkgs/components/assets/icons/small/AI-Cross-Naked.svg';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { CrossNakedIcon as Cross } from '@ev-tech/search-modules-shared/Icons';
import { Toggle } from '@base-ui/react/toggle';
import { Popover } from '@base-ui/react/popover';

import { useBreakpoint } from '../../hooks/useBreakpoint';

import styles from './Chip.module.css';

const ChipVariant = {
  default: 'default',
  ai: 'ai',
  button: 'button',
} as const;

type ChipVariant = (typeof ChipVariant)[keyof typeof ChipVariant];

interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onChange' | 'title' | 'value'> {
  className?: string;
  label: string | ReactNode;
  title?: string;
  icon?: ReactNode;
  variant?: ChipVariant;
  value?: string;
  isSelected?: boolean;
  defaultSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
  deleteButtonLabel?: string;
  onDelete?: () => void;
  'data-testid'?: string;
}

const chipVariants = cva(styles.baseClass, {
  variants: {
    variant: {
      [ChipVariant.default]: null,
      [ChipVariant.ai]: styles.aiWrapper,
      [ChipVariant.button]: styles.button,
    },
  },
});

const deleteButtonVariants = cva(styles.deleteButton, {
  variants: {
    variant: {
      [ChipVariant.default]: null,
      [ChipVariant.ai]: styles.aiDeleteButton,
      [ChipVariant.button]: null,
    },
  },
});

export const Chip = ({
  className,
  label,
  icon,
  variant = ChipVariant.default,
  deleteButtonLabel,
  onDelete,
  title,
  isSelected,
  defaultSelected,
  onChange,
  'data-testid': dataTestId,
  ...toggleProps
}: ChipProps) => {
  const chipRef = useRef<HTMLSpanElement>(null);
  const { breakpoint } = useBreakpoint();
  const isMobileAndTablet = (breakpoint ?? 0) <= Breakpoints.TABLET_LANDSCAPE;

  const handleOnDelete = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete?.();
  };

  if (variant === ChipVariant.button) {
    return (
      <Toggle
        {...toggleProps}
        pressed={isSelected}
        defaultPressed={defaultSelected}
        onPressedChange={onChange}
        className={cn(styles.baseClass, className)}
        data-testid={dataTestId ?? 'search-components_chip'}
      >
        <span className={styles.labelContainer}>
          {icon}
          <Copy className={styles.label} as="span" size="m" text={label} />
        </span>
      </Toggle>
    );
  }

  const labelContent = <Copy className={styles.label} as="span" size="m" text={label} />;
  const titleTrigger = isMobileAndTablet ? (
    <Popover.Trigger
      render={
        <button className={styles.labelContainer} type="button">
          {labelContent}
        </button>
      }
    />
  ) : (
    <span className={styles.labelContainer} tabIndex={0}>
      {labelContent}
    </span>
  );

  const chip = (
    <span
      {...(!isMobileAndTablet && title && { 'data-tooltip': title })}
      className={chipVariants({ variant, className })}
      data-testid={dataTestId ?? 'search-components_chip'}
      onKeyDown={(event) => {
        if ((event.key === 'Backspace' || event.key === 'Delete') && onDelete) {
          event.preventDefault();
          event.stopPropagation();
          onDelete();
        }
      }}
      ref={chipRef}
    >
      {icon}
      {title ? titleTrigger : <span className={styles.labelContainer}>{labelContent}</span>}
      {onDelete && (
        <button
          aria-label={deleteButtonLabel ?? (typeof label === 'string' ? 'Remove ' + label : 'Remove filter')}
          className={deleteButtonVariants({ variant })}
          onClick={handleOnDelete}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' || event.key === 'Delete') handleOnDelete(event);
          }}
          type="button"
        >
          {variant === ChipVariant.ai ? <AiCross aria-hidden="true" focusable="false" /> : <Cross aria-hidden="true" focusable="false" />}
        </button>
      )}
    </span>
  );

  if (isMobileAndTablet && title) {
    return (
      <Popover.Root>
        {chip}
        <Popover.Portal>
          <Popover.Positioner anchor={chipRef} side="top" align="center" sideOffset={3} collisionPadding={12} className={styles.positioner}>
            <Popover.Popup aria-label={title} className={styles.popover} initialFocus={false}>
              <div className={styles.popoverContent}>{title}</div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  return chip;
};
